// AgentTools that expose the analytics graph API to the in-browser
// Pi agent. They're thin wrappers around analyticsClient.js — same
// authedFetch path used by the rest of the dashboard, so tenant
// bearer + 401 refresh come for free.

import { Type } from "@earendil-works/pi-ai";
import { runQuery, describeSchema, rowsToObjects } from "./analyticsClient.js";
import { recordResult, getResult } from "./toolResultStore.js";
import { getScopeDimForColumn, pickLabelColumnForDim } from "./scopeColumns.js";
import { resolveStoredOrRehydrate } from "./toolResultRehydrate.js";

// Above this many rows, the LLM-visible content gets clipped to the
// first PREVIEW_HEAD_ROWS rows and a hint pointing at expand_preview
// / from_tool_calls. The full rows still land in toolResultStore so
// downstream tools can read them. Threshold picked so common
// dashboard queries (per-endpoint metrics, top-N agents) stay
// fully-inlined while large per-prompt scans (which the model rarely
// needs row-by-row anyway) skip the input-token tax.
const PREVIEW_ROW_THRESHOLD = 100;
const PREVIEW_HEAD_ROWS = 20;

// Byte caps, independent of row count. A handful of rows can still be
// enormous (raw EVENT rows carry full api_request bodies, megabytes
// each) — a 17-row result once inlined 29MB into the chat history and
// snowballed into a ~400MB persisted chat that crashed the browser.
//
// The inline cap is deliberately tuned to the CONTEXT CEILING, not to
// token thrift: a single result may fill most of the window when the
// model genuinely wants to read it, while anything larger could never
// survive to the model anyway — compaction would stub it silently one
// turn later. The cap converts that silent loss into an immediate,
// actionable preview. The live cap is MODEL-RELATIVE: factories take a
// `getInlineByteCap` closure (piChat derives it from the selected
// model's contextWindow, tracking mid-chat model switches); this
// constant is the no-closure fallback, sized for today's 1M-context
// flagships (~70% of the 700k-token compaction budget). The UI-facing
// `details` is clipped separately and much lower: it only feeds local
// table rendering. Full rows always land in toolResultStore regardless
// of clipping, so from_tool_calls / expand_preview still reach
// everything.
const MAX_INLINE_RESULT_BYTES = 2 * 1024 * 1024;
const MAX_DETAILS_RESULT_BYTES = 512 * 1024;
// When a single row alone exceeds the inline cap, expand_preview
// falls back to a truncated excerpt of that row's JSON.
const ROW_EXCERPT_BYTES = 128 * 1024;

function jsonLength(value) {
    try { return JSON.stringify(value)?.length || 0; } catch { return 0; }
}

function resolveInlineCap(getInlineByteCap) {
    const live = typeof getInlineByteCap === "function" ? Number(getInlineByteCap()) : 0;
    return live > 0 ? live : MAX_INLINE_RESULT_BYTES;
}

// Longest prefix of `rows` whose serialized size fits `budget` bytes.
// May return an empty array when even the first row doesn't fit.
function clipRowsToByteBudget(rows, budget) {
    const kept = [];
    let used = 0;
    for (const row of rows) {
        const size = jsonLength(row) || 64;
        if (used + size > budget) break;
        used += size;
        kept.push(row);
    }
    return kept;
}

// Column -> dim classification lives in scopeColumns.js (dependency-free so a
// Node test can import it); re-exported here for existing callers.
export { getScopeDimForColumn, pickLabelColumnForDim } from "./scopeColumns.js";

// Walk an analytics result for rows that look like entity references
// and emit a flat list of scope deeplinks. Each row contributes at
// most one scope (anchored on the first strict-id column we
// recognise) so we don't double-count e.g. session+endpoint
// columns. Rows without a valid id column don't get a chip at all —
// deeplinking by name doesn't work because the URL encoding and
// label cache both key off UUIDs. Capped at 12 to avoid papering
// the chat with chips when a query returns hundreds of rows.
function extractScopeLinks(result) {
    const objects = rowsToObjects(result);
    if (!objects.length) return [];

    // Find the first column that's a strict id source. Stable
    // across rows so the "primary entity" stays consistent.
    let primary = null;
    for (const col of result?.columns || []) {
        const dim = getScopeDimForColumn(col);
        if (dim) {
            primary = { col, dim };
            break;
        }
    }
    if (!primary) return [];

    // Pick a label column for this dim: prefer dim-specific labels
    // that are present in the result; fall back to generics; last
    // resort, just show the id.
    const labelCol = pickLabelColumnForDim(primary.dim, result.columns);

    const seen = new Set();
    const links = [];
    for (const row of objects) {
        const raw = row[primary.col];
        if (raw == null) continue;
        const id = String(raw);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const label = labelCol && row[labelCol] != null ? String(row[labelCol]) : id;
        links.push({ dim: primary.dim, ids: [id], label });
        if (links.length >= 12) break;
    }
    return links;
}

// `spec` is declared as Object (not Unknown) so the LLM tool-schema
// adapter generates `{"type":"object"}` and the model emits a JSON
// object, not a quoted string. Some providers fall back to a string
// type for unconstrained schemas, which made every analytics_query
// call hit the backend as a JSON-encoded string and fail to parse as
// a protobuf Query message ("proto: syntax error (line 1:10)").
// additionalProperties is left open because the spec's actual shape
// is the full Query proto — we don't want to enumerate it here and
// drift from the backend.
const querySchema = Type.Object({
    spec: Type.Object({}, {
        additionalProperties: true,
        description:
            "Analytics Query protobuf JSON (the inner Query, not wrapped in `query`). " +
            "Use `root` and `timeRange`, plus optional `filter`, `output`, `orderBy`, " +
            "`limit`, `offset`, `sample`, and `semanticSearch`. " +
            "`timeRange` is EITHER `{ \"absolute\": { \"start\": \"2026-01-01T00:00:00Z\", \"end\": \"2026-01-01T01:00:00Z\" } }` " +
            "(RFC 3339 UTC; the keys are exactly `start` and `end`) OR `{ \"relative\": { \"durationSeconds\": \"604800\" } }`. " +
            "Follow the complete runnable examples in the live analytics schema. Do not pass the MCP flat " +
            "DSL fields (`entity`, `joins`, `select`, `filters`, `metrics`, or `time_range_days`).",
    }),
});

// Defensive parse: even with the object schema above, some model
// turns will emit `spec` as a stringified JSON blob (especially when
// the model is mimicking a prior failed attempt). Accept either.
function coerceSpec(spec) {
    if (typeof spec !== "string") return spec;
    try { return JSON.parse(spec); } catch { return spec; }
}

const schemaSchema = Type.Object({});

function asTextResult(payload) {
    return {
        content: [{ type: "text", text: JSON.stringify(payload) }],
        details: payload,
    };
}

// Factory: every chat gets its own analytics_query instance so a
// successful call's rows land in the chat-scoped toolResultStore.
// execute_python.from_tool_calls reads from the same store via the
// shared getNamespaceKey closure, letting the agent reference prior
// query results by id instead of re-emitting the rows.
//
// Sub-agents share the parent's tool by reference (the parent's
// registry is what they pick from), so the namespace closure
// follows automatically — no separate plumbing needed.
// `onResult({ toolCallId, spec, objects })` is an optional observer for a
// successful call (the investigation graph feeds itself from it). It may
// return a short string, which lands in the payload as `_graph` so the model
// sees what the result added. It never fails the call.
export function createAnalyticsQueryTool({ getNamespaceKey, getInlineByteCap, onResult = null } = {}) {
    return {
        name: "analytics_query",
        label: "Analytics query",
        description:
            "Run a graph-traversal query against the analytics API. Pass the inner Query " +
            "protobuf JSON (`root`, `timeRange`, optional `filter` / `output` / " +
            "`orderBy` / `limit`) using the live analytics schema's runnable examples. " +
            "Do not pass the MCP flat DSL (`entity`, `joins`, `select`, `filters`, `metrics`). " +
            "Returns { columns, rows, cost, resolvedSpec, _tool_call_id }.\n\n" +
            "Large results auto-preview: if the result has more than " +
            `${PREVIEW_ROW_THRESHOLD} rows, only the first ${PREVIEW_HEAD_ROWS} rows ` +
            "come back in the tool content (with a `_preview` block describing what " +
            "you've got). The FULL rows are always stored under `_tool_call_id` and " +
            "can be reached two ways:\n" +
            "  • `execute_python` with `from_tool_calls: { <var>: \"<id>\" }` — pipes " +
            "the full rows into a python global without re-emitting them in args. " +
            "This is the right move whenever you're about to compute over the rows.\n" +
            "  • `expand_preview({ tool_call_id, offset, limit })` — pulls a specific " +
            "window of rows back into context. Use only when you genuinely need to " +
            "READ the rows (e.g. confirm a specific value is present).\n\n" +
            "Small results (≤ " + PREVIEW_ROW_THRESHOLD + " rows) are inlined verbatim; " +
            "no preview wrapper, no expand step needed.",
        parameters: querySchema,
        async execute(toolCallId, { spec }) {
            // A chat-driven query must observe current state on every call,
            // never a cached wire result — bypass runQuery's result cache.
            const coerced = coerceSpec(spec);
            const result = await runQuery(coerced, { cache: false });
            // Stash row-shaped result by tool call id. rowsToObjects
            // flattens {values:[...]} into plain row dicts — the shape
            // every downstream consumer (pandas, JS predicates)
            // expects.
            let objects = null;
            try {
                const ns = getNamespaceKey?.() || "default";
                objects = rowsToObjects(result);
                recordResult(ns, toolCallId, objects, "analytics_query");
            } catch { /* recording is non-essential — never fail the call */ }
            let graphNote = null;
            if (onResult && objects) {
                try { graphNote = onResult({ toolCallId, spec: coerced, objects }) || null; } catch { graphNote = null; }
            }
            const llmResult = buildLlmResult(result, toolCallId, resolveInlineCap(getInlineByteCap));
            // The observer returns either a note for the model or
            // { note, graph }: the note rides in the content, the graph
            // snapshot rides in details for the chat UI only.
            const note = typeof graphNote === "string" ? graphNote : graphNote?.note;
            const snapshot = graphNote && typeof graphNote === "object" ? graphNote.graph || null : null;
            if (typeof note === "string" && note) llmResult._graph = note;
            // `details` feeds the chat UI: scope deeplink chips plus the
            // result table. It doesn't go back into the model's content
            // (only the JSON dump in `text` does).
            const details = buildDetails(result, extractScopeLinks(result));
            if (snapshot) details.graph = snapshot;
            return {
                content: [{ type: "text", text: JSON.stringify(llmResult) }],
                details,
            };
        },
    };
}

// Back-compat shim for any caller that imports the singleton
// (test harnesses, scripts that pre-date the factory). Records
// into the "default" namespace which never collides with a live
// chat's keys.
export const analyticsQueryTool = createAnalyticsQueryTool({ getNamespaceKey: () => "default" });

// Build the LLM-visible payload. When the result is small, the model
// sees the rows verbatim — cheap to read and useful for chain-of-
// thought. When the result is large — by row count OR by bytes — the
// model sees a head sample plus a precise hint telling it how to get
// the rest (without re-emitting rows in its own output tokens). The
// store still has the full rows either way.
//
// The truncation marker is `_preview` rather than `rows: […more…]`
// so downstream parsers that look at `rows[].values` don't trip
// over a synthetic placeholder row — they just see fewer rows.
function buildLlmResult(result, toolCallId, inlineCapBytes) {
    const allRows = Array.isArray(result.rows) ? result.rows : [];
    const totalRows = allRows.length;
    const base = { ...result, _tool_call_id: toolCallId };

    const headCount = totalRows > PREVIEW_ROW_THRESHOLD ? PREVIEW_HEAD_ROWS : totalRows;
    let shown = allRows.slice(0, headCount);
    let byteClipped = false;
    if (jsonLength(shown) > inlineCapBytes) {
        shown = clipRowsToByteBudget(shown, inlineCapBytes);
        byteClipped = true;
    }
    if (shown.length === totalRows && !byteClipped) return base;

    const reason = byteClipped
        ? `Result is ~${Math.round(jsonLength(allRows) / 1024)} KB across ${totalRows} rows — ` +
          `over the ${Math.round(inlineCapBytes / 1024)} KB inline cap (sized to this model's ` +
          `context window), so only ` +
          `${shown.length} row${shown.length === 1 ? "" : "s"} are shown` +
          (shown.length === 0 ? " (even a single row exceeds the cap)" : "") + `. `
        : `Result has ${totalRows} rows; showing first ${shown.length} ` +
          `to keep the next-turn input small. `;
    return {
        ...base,
        rows: shown,
        _preview: {
            shown: shown.length,
            total: totalRows,
            note:
                reason +
                `Options for the remaining ${totalRows - shown.length} rows:\n` +
                `  • Pass the full rows into Python without re-emitting them: ` +
                `execute_python with from_tool_calls: { <var>: "${toolCallId}" }. ` +
                `This never enters the chat context — always the right move for computing over big rows.\n` +
                `  • Read a specific slice into context: ` +
                `expand_preview({ tool_call_id: "${toolCallId}", offset: ${shown.length}, limit: 100 }).\n` +
                `  • Re-run analytics_query selecting fewer / smaller fields, or with a tighter ` +
                `filter, if you only need a subset.`,
        },
    };
}

// UI-facing `details` gets the same byte discipline at a higher cap.
// The chat's result table reads rows out of details — a multi-MB
// details payload rides along in React state, every turn snapshot,
// and the persisted chat record.
function buildDetails(result, scopes) {
    const annotated = { ...result, scopes };
    const allRows = Array.isArray(result.rows) ? result.rows : [];
    const totalBytes = jsonLength(allRows);
    if (totalBytes <= MAX_DETAILS_RESULT_BYTES) return annotated;
    const kept = clipRowsToByteBudget(allRows, MAX_DETAILS_RESULT_BYTES);
    return {
        ...annotated,
        rows: kept,
        _clippedForSize: {
            shownRows: kept.length,
            totalRows: allRows.length,
            estimatedBytes: totalBytes,
        },
    };
}

// Default page size for `expand_preview`. Tuned to match
// PREVIEW_ROW_THRESHOLD so the model can page through a large
// result one window at a time without each window itself crossing
// the preview cutoff. Cap on the requested limit is generous (1000)
// because if the model really wants the whole thing inline it's
// telling us so deliberately.
const EXPAND_DEFAULT_LIMIT = 100;
const EXPAND_MAX_LIMIT = 1000;

const expandSchema = Type.Object({
    tool_call_id: Type.String({
        description:
            "The `_tool_call_id` echoed in the prior tool result. Identifies which stored " +
            "row-shaped result to page through. Same id you'd use in execute_python's " +
            "from_tool_calls.",
    }),
    offset: Type.Optional(
        Type.Number({
            description:
                "Zero-based row index to start at. Default 0. To continue after the " +
                "preview, pass the preview's `shown` count (typically 20).",
        }),
    ),
    limit: Type.Optional(
        Type.Number({
            description:
                `Max rows to return in this window. Default ${EXPAND_DEFAULT_LIMIT}, ` +
                `cap ${EXPAND_MAX_LIMIT}.`,
        }),
    ),
});

// Factory mirrors createAnalyticsQueryTool so the per-chat namespace
// closure is shared. expand_preview reads the same toolResultStore
// that analytics_query writes to — no separate paging cache, no
// re-running the underlying query.
export function createExpandPreviewTool({ getNamespaceKey, rehydrate, getInlineByteCap } = {}) {
    return {
        name: "expand_preview",
        label: "Expand preview",
        description:
            "Page into a row-shaped tool result that was previewed earlier. Use this " +
            "when you need to actually READ rows past the preview head — for example, " +
            "to confirm a specific endpoint appears in the result, or to see the tail " +
            "of a ranked list.\n\n" +
            "When NOT to use:\n" +
            "  • You're going to compute over the rows — call `execute_python` with " +
            "`from_tool_calls: { <var>: \"<tool_call_id>\" }` instead. That pipes the " +
            "FULL result into the python namespace without ever sending the rows back " +
            "through the model.\n" +
            "  • You want a different shape of the underlying data — re-run " +
            "`analytics_query` with a tighter filter.\n\n" +
            "Returns `{ rows, offset, limit, total, _tool_call_id }`. Rows are plain " +
            "objects keyed by the original result's columns.",
        parameters: expandSchema,
        async execute(_toolCallId, { tool_call_id, offset, limit }, signal) {
            const ns = getNamespaceKey?.() || "default";
            // Phase 3: if the rows have been evicted (budget cap or
            // never persisted), `resolveStoredOrRehydrate` re-runs
            // the original analytics_query spec to repopulate the
            // store before continuing.
            const { stored, rehydrated, reason } = await resolveStoredOrRehydrate({
                namespaceKey: ns,
                toolCallId: tool_call_id,
                rehydrate,
                signal,
            });
            if (!stored || !Array.isArray(stored.rows)) {
                return {
                    content: [{
                        type: "text",
                        text: `error: no stored result for tool_call_id ${tool_call_id}` +
                            (reason ? ` (${reason})` : "") + `. ` +
                            `The id must come from the \`_tool_call_id\` field of an earlier ` +
                            `row-producing tool result (analytics_query or execute_python).`,
                    }],
                    isError: true,
                };
            }
            const total = Array.isArray(stored.rows) ? stored.rows.length : 0;
            const start = Math.max(0, Math.floor(Number(offset) || 0));
            const requested = Math.max(1, Math.floor(Number(limit) || EXPAND_DEFAULT_LIMIT));
            const window = Math.min(requested, EXPAND_MAX_LIMIT);
            let rows = stored.rows.slice(start, start + window);
            // Row-count paging isn't enough — a window of huge rows can
            // re-inline megabytes. Clip the window by bytes; when even
            // one row exceeds the cap, fall back to a truncated excerpt
            // of that row's JSON so the model can still peek inside.
            const inlineCap = resolveInlineCap(getInlineByteCap);
            let rowExcerpt = null;
            const byteCapped = clipRowsToByteBudget(rows, inlineCap);
            if (byteCapped.length < rows.length) {
                if (byteCapped.length === 0 && rows.length > 0) {
                    const rowJson = JSON.stringify(rows[0]) || "";
                    rowExcerpt = {
                        row_excerpt: rowJson.slice(0, ROW_EXCERPT_BYTES),
                        row_bytes: rowJson.length,
                        _row_truncated: true,
                    };
                    rows = [];
                } else {
                    rows = byteCapped;
                }
            }
            const consumed = rowExcerpt ? 1 : rows.length;
            const payload = {
                rows,
                ...(rowExcerpt || {}),
                offset: start,
                limit: window,
                total,
                source: stored.source,
                _tool_call_id: tool_call_id,
                ...(rehydrated ? { _rehydrated: true } : {}),
            };
            const nextOffset = start + consumed;
            if (nextOffset < total || rowExcerpt) {
                payload._more = {
                    remaining: total - nextOffset,
                    next_offset: nextOffset,
                    note:
                        (rowExcerpt
                            ? `Row at offset ${start} serializes to ~${Math.round(rowExcerpt.row_bytes / 1024)} KB — ` +
                              `too large to inline whole; you got the first ${Math.round(ROW_EXCERPT_BYTES / 1024)} KB. ` +
                              `To work with full rows this size, pipe them into execute_python via ` +
                              `from_tool_calls and slice the fields you need there. `
                            : consumed < window && nextOffset < total
                                ? `Window clipped at ~${Math.round(inlineCap / 1024)} KB (${consumed} of ` +
                                  `${Math.min(window, total - start)} requested rows). `
                                : "") +
                        (nextOffset < total
                            ? `${total - nextOffset} more rows available. ` +
                              `Call expand_preview again with offset=${nextOffset} to continue, ` +
                              `or hand the full result to execute_python via from_tool_calls.`
                            : ""),
                };
            }
            return {
                content: [{ type: "text", text: JSON.stringify(payload) }],
                details: payload,
            };
        },
    };
}

export const analyticsSchemaTool = {
    name: "analytics_describe_schema",
    label: "Describe analytics schema",
    description:
        "Return the analytics graph schema: available entities, fields per entity, " +
        "and valid traversal edges. Call this before composing a query if the " +
        "catalogue is unknown.",
    parameters: schemaSchema,
    async execute() {
        const result = await describeSchema();
        return asTextResult(result);
    },
};
